import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, Trophy, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const MatchingGame = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [timeLeft, setTimeLeft] = useState(60);
  const [score, setScore] = useState(0);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [matched, setMatched] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const pairs = [
    { term: 'Blockchain', definition: 'A distributed ledger that records transactions across multiple computers' },
    { term: 'Hash', definition: 'A unique fixed-size string that represents data of any size' },
    { term: 'Smart Contract', definition: 'Self-executing code that automatically enforces agreement terms' },
    { term: 'Mining', definition: 'The process of validating transactions and adding blocks to the chain' },
    { term: 'Consensus', definition: 'A mechanism for nodes to agree on the blockchain state' },
    { term: 'DeFi', definition: 'Financial services built on blockchain without intermediaries' },
    { term: 'NFT', definition: 'A unique digital token representing ownership of a specific asset' },
    { term: 'Gas Fee', definition: 'The cost paid to execute transactions on a blockchain network' },
  ];

  const [terms] = useState(pairs.map(p => p.term).sort(() => Math.random() - 0.5));
  const [definitions] = useState(pairs.map(p => p.definition).sort(() => Math.random() - 0.5));

  useEffect(() => {
    if (gameStarted && timeLeft > 0 && matched.length < pairs.length) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 || matched.length === pairs.length) {
      endGame();
    }
  }, [timeLeft, matched, gameStarted]);

  useEffect(() => {
    if (selectedTerm && selectedDefinition) {
      checkMatch();
    }
  }, [selectedTerm, selectedDefinition]);

  const startGame = () => {
    setGameStarted(true);
  };

  const checkMatch = () => {
    const pair = pairs.find(p => p.term === selectedTerm && p.definition === selectedDefinition);
    if (pair) {
      setMatched([...matched, pair]);
      setScore(score + 100);
      toast.success('Perfect match! +100 points');
    } else {
      toast.error('Incorrect match. Try again!');
    }
    setSelectedTerm(null);
    setSelectedDefinition(null);
  };

  const endGame = async () => {
    if (gameOver) return;
    setGameOver(true);
    
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'matching',
        score: score,
        time_taken: 60 - timeLeft
      });
      toast.success(`Game complete! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  const isMatched = (item, type) => {
    return matched.some(m => type === 'term' ? m.term === item : m.definition === item);
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Matching Game</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Match blockchain terms with their correct definitions. You have 60 seconds to match all 8 pairs. Each correct match earns you 100 points!
            </p>
            <Button
              onClick={startGame}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Game
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              data-testid="back-button"
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Game Complete!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Final Score</div>
              <div className="mt-4 text-zinc-400">
                Matched: <span className="text-primary font-mono">{matched.length}/{pairs.length}</span>
              </div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Play Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              data-testid="back-to-games-button"
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Game
        </Button>

        {/* Game Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-grotesk font-bold text-white">Matching Game</h1>
            <p className="text-zinc-400 text-sm">Match terms with definitions</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-center" data-testid="game-timer">
              <Clock className="w-5 h-5 text-primary mx-auto mb-1" />
              <div className="text-2xl font-mono font-bold text-primary">{timeLeft}s</div>
            </div>
            <div className="text-center" data-testid="game-score">
              <Trophy className="w-5 h-5 text-primary mx-auto mb-1" />
              <div className="text-2xl font-mono font-bold text-white">{score}</div>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="text-sm text-zinc-400 mb-2">Matched: {matched.length} / {pairs.length}</div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(matched.length / pairs.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Game Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Terms Column */}
          <div>
            <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-4">Terms</h3>
            <div className="space-y-3">
              {terms.map((term) => (
                <button
                  key={term}
                  onClick={() => !isMatched(term, 'term') && setSelectedTerm(term)}
                  disabled={isMatched(term, 'term')}
                  data-testid={`term-${term.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full p-4 rounded-lg text-left font-medium transition-all ${
                    isMatched(term, 'term')
                      ? 'bg-primary/20 border-2 border-primary text-primary cursor-not-allowed'
                      : selectedTerm === term
                      ? 'bg-primary text-black border-2 border-primary scale-105'
                      : 'bg-zinc-900/50 border-2 border-white/10 text-white hover:border-primary/50'
                  }`}
                >
                  {term}
                </button>
              ))}
            </div>
          </div>

          {/* Definitions Column */}
          <div>
            <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-4">Definitions</h3>
            <div className="space-y-3">
              {definitions.map((definition) => (
                <button
                  key={definition}
                  onClick={() => !isMatched(definition, 'definition') && setSelectedDefinition(definition)}
                  disabled={isMatched(definition, 'definition')}
                  data-testid={`definition-${definition.substring(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full p-4 rounded-lg text-left text-sm transition-all ${
                    isMatched(definition, 'definition')
                      ? 'bg-primary/20 border-2 border-primary text-primary cursor-not-allowed'
                      : selectedDefinition === definition
                      ? 'bg-primary text-black border-2 border-primary scale-105'
                      : 'bg-zinc-900/50 border-2 border-white/10 text-white hover:border-primary/50'
                  }`}
                >
                  {definition}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingGame;
